import { Component, ChangeDetectorRef } from '@angular/core';
import { CmsserviceService } from '../../common-cms-service/cmsservice.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';

import { FormsModule } from '@angular/forms';

import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-free-assessment',
  standalone: true,

  imports: [

    CommonModule,

    FormsModule,

    RouterModule

  ],
  templateUrl: './free-assessment.component.html',
  styleUrl: './free-assessment.component.css',
})
export class FreeAssessmentComponent {
  title = 'Free 11+ Assessment | Sehyog';

  // ── Filter model ───────────────────────────────────────────
  filters: any = {
    pattern: null,
    search: ''
  };

  // ── Filter options ─────────────────────────────────────────
  patternTypes: any[] = [];

  // ── Data ───────────────────────────────────────────────────
  freeTestList: any[] = [];
  private allFreeResults: any[] = [];

  // ── State ──────────────────────────────────────────────────
  ngUnsubscribe = new Subject<void>();
  openFaq: number = -1;

  // ── Static content ─────────────────────────────────────────
  testimonials: any[] = [
    {
      name: 'Priya S., Ilford',
      text: 'The free assessment opened our eyes to exactly where our daughter needed help. The RAR was incredibly detailed and the PTM gave us a clear, actionable plan. We had no idea Sehyog offered all of this for free.'
    },
    {
      name: 'Raj Mehta, Chelmsford',
      text: 'We booked the free assessment not expecting much, but the RAR report was as detailed as any paid service we had seen. The PTM with the tutor lasted 40 minutes and answered every question we had.'
    },
    {
      name: 'Anita Patel, Harrow',
      text: 'My son was nervous about the 11+ and we didn\'t know where to start. The free test showed us exactly which topics needed work, and the teacher gave us a step-by-step plan. Highly recommend to every 11+ parent.'
    }
  ];

  faqs: any[] = [
    {
      q: 'Is the assessment really 100% free?',
      a: 'Yes, completely. The online test, the detailed RAR report and the Parent–Teacher Meeting are all free of charge. There is no payment required, no credit card needed, and no obligation to purchase any other service.'
    },
    {
      q: 'How does the online assessment work?',
      a: 'After booking a slot, your child takes a full-length 11+ diagnostic paper online at home. The test is timed and mirrors real exam conditions. You will need a device with a browser and a stable internet connection.'
    },
    {
      q: 'What is the RAR report?',
      a: 'The Result Analysis Report (RAR) is a detailed breakdown of your child\'s performance. It covers their overall score and percentile, topic-by-topic analysis across Maths, English and Reasoning, clearly highlighted strengths and weak areas, cohort benchmarking, and the gap to grammar school entry standard.'
    },
    {
      q: 'When will we receive the RAR report?',
      a: 'The RAR report is delivered within 48 hours of your child completing the assessment. You will receive it by email.'
    },
    {
      q: 'What happens in the PTM (Parent–Teacher Meeting)?',
      a: 'The PTM is a 30–45 minute 1-to-1 online meeting with an experienced 11+ tutor. The tutor walks you through the RAR report, explains priority improvement areas, shares exam strategy and time-management guidance, recommends a preparation pathway, and answers any questions you have about the 11+, school selection or CAF forms.'
    },
    {
      q: 'Which exam boards does the free assessment cover?',
      a: 'The free assessment covers the major 11+ exam formats including GL, CSSE and FSCE. The specific paper for your session is shown on the booking card. If you are unsure which board is right for your target school, the tutor will advise during the PTM.'
    },
    {
      q: 'How do I book?',
      a: 'Scroll to the "Available Free Assessment Dates" section, find a date that suits you and click the green "Book Free" button. Registration takes under 60 seconds.'
    }
  ];

  // ── Constructor ────────────────────────────────────────────
  constructor(
    private router: ActivatedRoute,
    private titleService: Title,
    private categoryProduct: CmsserviceService,
    private route: Router,
    private ref: ChangeDetectorRef
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────
  ngOnInit(): void {
    this.titleService.setTitle(this.title);
    this.loadFiltersAndData();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // ── Load filter options then all FREE tests ────────────────
  private loadFiltersAndData(): void {
    const self = this;

    this.categoryProduct.getTestFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: any) => {
        const data = (res && res.data) ? res.data : {};
        const allTypes = data.type ? data.type : [];

        const allowedPatternNames = [
          'GL', 'CEM', 'CSSE', 'FSCE', 'SET',
          'GL Redbridge', 'GL Kent', 'GLQE Barnet', 'GL Bucks', 'GL Bexley', 'GL HBS'
        ];
        self.patternTypes = allTypes.filter((t: any) =>
          allowedPatternNames.indexOf(t.type) !== -1
        );

        self.fetchAllFreeTests();
      }, (err: any) => {
        console.error('getTestFilters error', err);
        self.fetchAllFreeTests();
      });
  }

  // ── Fetch all pages, keep only free 11+ tests ─────────────
  private fetchAllFreeTests(): void {
    const self = this;
    this.allFreeResults = [];
    const maxPages = 50;
    let pagesFetched = 0;

    const finalise = () => {
      self.applyFilters();
      self.ref.detectChanges();
    };

    const fetchPage = (pageNum: number) => {
      if (pageNum > maxPages) { finalise(); return; }
      pagesFetched++;

      const userId = +(localStorage.getItem('userid') || 0);
      const formData: any = {
        release: 'new',
        exam_type: 'online',   // free assessments are online only
        search: '',
        course: [],
        category: [],
        type: [],
        user_id: userId,
        page: pageNum
      };

      self.categoryProduct.getTestList(formData)
        .pipe(takeUntil(self.ngUnsubscribe))
        .subscribe((res: any) => {
          const chunk: any[] = (res && res.data) ? res.data : [];
          if (chunk.length === 0) { finalise(); return; }

          // Keep only free 11+ tests
          const freePlusChunk = chunk.filter((t: any) =>
            self.isElevenPlus(t) && self.isFree(t)
          );
          self.allFreeResults = self.allFreeResults.concat(freePlusChunk);

          const extra = (res && res.extra && typeof res.extra.extraCount === 'number')
            ? res.extra.extraCount : null;

          if (extra !== null) {
            if (extra > 0) { fetchPage(pageNum + 1); } else { finalise(); }
          } else {
            fetchPage(pageNum + 1);
          }
        }, (err: any) => {
          console.error('Error fetching free tests', err);
          finalise();
        });
    };

    fetchPage(1);
  }

  // ── Is 11+ test ────────────────────────────────────────────
  private isElevenPlus(t: any): boolean {
    if (!t) return false;
    const cid        = Number(t.category_id);
    const courseName = (t.course || '').toString().toLowerCase();
    const courseId   = Number(t.course_id);
    const name       = (t.test_name || '').toString().toLowerCase();
    const ELEVENPLUS_CATEGORY_IDS = [44];
    return (
      courseName === '11plus' || courseId === 1 ||
      ELEVENPLUS_CATEGORY_IDS.indexOf(cid) !== -1 ||
      name.indexOf('11+') !== -1 || name.indexOf('11plus') !== -1
    );
  }

  // ── Is free test ───────────────────────────────────────────
  private isFree(t: any): boolean {
    if (!t) return false;
    const payMethod = (t.payment_method || '').toString().toLowerCase();
    const fees      = parseFloat(t.test_fees) || 0;
    return payMethod === 'free' || fees === 0;
  }

  // ── Public filter methods ──────────────────────────────────
  applyFilters(): void {
    let filtered = this.allFreeResults.slice();

    // Board filter
    if (this.filters.pattern != null) {
      const patternMeta = this.patternTypes.find((p: any) =>
        String(p.type_id) === String(this.filters.pattern)
      );
      const patternLabel = patternMeta ? (patternMeta.type || '').toLowerCase() : '';
      if (patternLabel) {
        filtered = filtered.filter((t: any) =>
          ((t.type || '') + '').toLowerCase().indexOf(patternLabel) !== -1 ||
          ((t.test_name || '') + '').toLowerCase().indexOf(patternLabel) !== -1
        );
      }
    }

    // Search filter
    if (this.filters.search) {
      const q = (this.filters.search + '').toLowerCase().trim();
      if (q) {
        filtered = filtered.filter((t: any) =>
          ((t.test_name || '') + '').toLowerCase().indexOf(q) !== -1 ||
          ((t.all_location || t.location || '') + '').toLowerCase().indexOf(q) !== -1 ||
          ((t.category || '') + '').toLowerCase().indexOf(q) !== -1 ||
          ((t.type || '') + '').toLowerCase().indexOf(q) !== -1
        );
      }
    }

    this.freeTestList = this.sortUpcomingFirst(filtered);
    this.ref.detectChanges();
  }

  resetFilters(): void {
    this.filters = { pattern: null, search: '' };
    this.applyFilters();
  }

  // ── Date parsing ───────────────────────────────────────────
  private monthMap: any = { JAN:0,FEB:1,MAR:2,APR:3,MAY:4,JUN:5,JUL:6,AUG:7,SEP:8,OCT:9,NOV:10,DEC:11 };
  private fullMonthMap: any = { JANUARY:0,FEBRUARY:1,MARCH:2,APRIL:3,MAY:4,JUNE:5,JULY:6,AUGUST:7,SEPTEMBER:8,OCTOBER:9,NOVEMBER:10,DECEMBER:11 };

  private parseDateString(s: string): number | null {
    if (!s) return null;
    const str = s.trim();
    let m: any;

    m = str.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (m) {
      let y = parseInt(m[3], 10);
      if (y < 100) y += 2000;
      return Date.UTC(y, parseInt(m[2], 10) - 1, parseInt(m[1], 10));
    }
    m = str.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) return Date.UTC(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));

    m = str.match(/(?:from\s+)?(?:mon|tue|wed|thu|fri|sat|sun)?\s*([A-Za-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s*(\d{4}))?/i);
    if (m) {
      const monName = m[1].toUpperCase();
      const dd = parseInt(m[2], 10);
      const yy = m[3] ? parseInt(m[3], 10) : (new Date()).getFullYear();
      let monIdx = this.fullMonthMap[monName];
      if (monIdx == null) monIdx = this.monthMap[monName.substring(0, 3)];
      if (monIdx != null) return Date.UTC(yy, monIdx, dd);
    }
    m = str.match(/(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})/);
    if (m) {
      const mn = m[2].toUpperCase();
      let mi = this.fullMonthMap[mn];
      if (mi == null) mi = this.monthMap[mn.substring(0, 3)];
      if (mi != null) return Date.UTC(parseInt(m[3], 10), mi, parseInt(m[1], 10));
    }
    return null;
  }

  private getTestTimestamp(t: any): number {
    const td = this.parseDateString((t && t.test_date) || '');
    if (td != null) return td;
    const tn = this.parseDateString((t && t.test_name) || '');
    if (tn != null) return tn;
    return Number.NaN;
  }

  private sortUpcomingFirst(list: any[]): any[] {
    const now = Date.now();
    const future: any[] = [], past: any[] = [], unknown: any[] = [];
    for (const item of list) {
      const ts = this.getTestTimestamp(item);
      if (isNaN(ts))   { unknown.push(item); }
      else if (ts >= now) { future.push({ ts, item }); }
      else                { past.push({ ts, item }); }
    }
    future.sort((a, b) => a.ts - b.ts);
    past.sort((a, b) => b.ts - a.ts);
    return [
      ...future.map(x => x.item),
      ...past.map(x => x.item),
      ...unknown
    ];
  }

  // ── Template helpers ───────────────────────────────────────
  trimChar(str: string, length: number = 40): string {
    if (!str) return '';
    return str.length < length ? str : str.substring(0, length) + '...';
  }

  hasDate(dateStr: string): boolean {
    if (!dateStr) return false;
    return this.parseDateString(dateStr) != null;
  }

  private getDateObj(dateStr: string): Date | null {
    if (!dateStr) return null;
    const ts = this.parseDateString(dateStr);
    return ts != null ? new Date(ts) : null;
  }

  getDateDay(dateStr: string): string {
    const d = this.getDateObj(dateStr);
    return d ? String(d.getUTCDate()) : '';
  }

  getDateMon(dateStr: string): string {
    const d = this.getDateObj(dateStr);
    if (!d) return '';
    return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()];
  }

  getDateYr(dateStr: string): string {
    const d = this.getDateObj(dateStr);
    return d ? String(d.getUTCFullYear()) : '';
  }

  isRangeDate(dateStr: string): boolean {
    if (!dateStr) return false;
    return /^(from\s+)/i.test(dateStr.trim());
  }

  toggleFaq(i: number): void {
    this.openFaq = this.openFaq === i ? -1 : i;
  }

  // ── Scroll helpers ─────────────────────────────────────────
  scrollToTests(): void {
    const el = document.getElementById('free-tests-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  scrollToHowItWorks(): void {
    const el = document.getElementById('how-it-works');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }
}
